import type {ReactNode} from 'react';
import {useMemo} from 'react';
import {useAppSelector} from '@/hooks';
import {useAttachPagePreviewRefreshListener} from '@Hooks/poster-editor/useAttachPagePreviewRefreshListener';
import type {PagePreviewConfig} from '@Hooks/poster-editor/useAttachPagePreviewRefreshListener';
import {MobilePageSelectionModeState} from '@Components/poster-editor/poster-editor.types';
import {MODALS_IDS} from '@Components/modal-container';
import {DOWNLOAD_MODAL_PANEL_ID} from '@Components/modals/download-modal/download-modal.library';
import {DOWNLOAD_PREVIEW_DIMENSION, TIMELINE_PREVIEW_DIMENSION, MOBILE_PAGE_SELECTION_PREVIEW_DIMENSION} from './pages-preview-thumb-manager.types';

export function PagesPreviewThumbManager(): ReactNode {
  const isTimelineModeActive = useAppSelector((state) => {
    return state.posterEditor.isTimelineModeActive === true;
  });
  const isMobilePageSelectionActive = useAppSelector((state) => {
    return state.posterEditor.mobilePageSelectionModeState === MobilePageSelectionModeState.ACTIVE;
  });
  const isDownloadPanelOpen = useAppSelector((state) => {
    return state.downloadDialog.panelOpenState === true;
  });
  const isDownloadModalOpen = useAppSelector((state) => {
    const isOpen = state.modals.modalsHashmap[MODALS_IDS.DOWNLOAD_MODAL]?.isOpen ?? false;
    const isHidden = state.panels.panelHashmap[DOWNLOAD_MODAL_PANEL_ID]?.isHidden ?? false;
    return isOpen && !isHidden;
  });

  const isDownloadOpen = isDownloadPanelOpen || isDownloadModalOpen;
  const isAllPagesActive = isMobilePageSelectionActive || isDownloadOpen;

  const allPagesDimension = useMemo((): number => {
    if (isMobilePageSelectionActive) {
      return MOBILE_PAGE_SELECTION_PREVIEW_DIMENSION;
    }
    if (isDownloadOpen) {
      return DOWNLOAD_PREVIEW_DIMENSION;
    }
    return 0;
  }, [isMobilePageSelectionActive, isDownloadOpen]);

  const config: PagePreviewConfig = useMemo(() => {
    return {
      allPagesBatchedPreviews: {
        enabled: isAllPagesActive,
        requiredDimension: allPagesDimension,
      },
      currentPageLivePreview: {
        enabled: isTimelineModeActive,
        requiredDimension: TIMELINE_PREVIEW_DIMENSION,
      },
      allPagesLivePreview: {
        enabled: isMobilePageSelectionActive,
        requiredDimension: MOBILE_PAGE_SELECTION_PREVIEW_DIMENSION,
      },
    };
  }, [isAllPagesActive, allPagesDimension, isTimelineModeActive, isMobilePageSelectionActive]);

  useAttachPagePreviewRefreshListener(config);

  return null;
}
